import React, { Component } from 'react';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';

import lodashCompact from 'lodash/fp/compact';

import { SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG } from 'api/utilityActionTypes';

import { SCREEN_ID_UTILITY_SETUP_ADDRESS_SEARCH } from 'client/constants/screenIds';

import { hasRoleRenter, hasRoleForGroupPartner, hasRoleForSrStaff } from 'common/userRoles';

import { getRenterUtilityLink, isUtilityActivationActive } from 'common/utility/helpers';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import { withUtilityApiState } from 'client/helpers/withApiState';

import branch from 'client/core/branch';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityPartnerAddressActions from 'client/actions/UtilityPartnerAddress';

import SrUtilitySetupAddressSearchPage from 'client/components/SrUtilitySetup/SrUtilitySetupAddressSearchPage';

const shouldRedirectToProfilePage = (currentUser) => {
  return (
    hasRoleRenter(currentUser.roles) &&
    !hasRoleForGroupPartner(currentUser.roles) &&
    !hasRoleForSrStaff(currentUser.roles)
  );
};

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_SETUP_ADDRESS_SEARCH)
@withScrollToTop
@withLocationQuery
@branch(({ match, locationQuery }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityUser: ['utilityUser'],
  currentUser: ['currentUser'],
  utilityActivationLazySignupShortId: ['utilityActivations', 'lazySignupShortId'],
  ...(locationQuery.activationLinkRequestId && {
    activationLinkRequest: [
      'utilityActivationLinkRequests',
      'byId',
      locationQuery.activationLinkRequestId,
    ],
  }),
}))
@branch(({ utilityUser, utilityActivationLazySignupShortId }) => {
  const branch = {};

  if (utilityUser.id) {
    branch.userUtilityActivation = ['utilityActivations', 'byUtilityUserId', utilityUser.id];
  }

  if (utilityActivationLazySignupShortId) {
    branch.lazySignupUtilityActivation = [
      'utilityActivations',
      'byShortId',
      utilityActivationLazySignupShortId,
    ];
  }

  return branch;
})
@branch(({ utilityPartner }) => {
  if (utilityPartner) {
    return {
      recentlyUnlistedAddresses: [
        'utilityPartnerAddresses',
        'recentlyUnlistedByPartnerId',
        utilityPartner.id,
      ],
    };
  }
  return {};
})
@withUtilityApiState({
  getUtilityPartnerApiState: {
    action: SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG,
  },
})
class UtilitySetupAddressSearchPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Setup | Address Search',
  });

  static fetchData(match, inject) {
    const utilityActivationLazySignupShortId = inject.state.get([
      'utilityActivations',
      'lazySignupShortId',
    ]);
    const locationQuery = queryString.parse(inject.location.search);
    const activationLinkRequestId = parseInt(locationQuery.activationLinkRequestId, 10);

    return Promise.all(
      lodashCompact([
        UtilityPartnerActions.getBySlug(inject, {
          slug: match.params.partnerSlug,
        }).then((utilityPartner) => {
          if (!utilityPartner) {
            return;
          }

          return Promise.all(
            lodashCompact([
              UtilityPartnerAddressActions.getRecentlyUnlistedAddresses(inject, {
                utilityPartnerId: utilityPartner.id,
              }),
              activationLinkRequestId &&
                UtilityActivationActions.getLinkRequestById(inject, {
                  activationLinkRequestId,
                }),
            ]),
          );
        }),
        utilityActivationLazySignupShortId &&
          UtilityActivationActions.getByShortId(inject, {
            utilityActivationShortId: utilityActivationLazySignupShortId,
          }),
      ]),
    );
  }

  render() {
    const {
      currentUser,
      activationLinkRequest,
      userUtilityActivation,
      lazySignupUtilityActivation,
      utilityUser,
      utilityPartner,
      recentlyUnlistedAddresses,
      getUtilityPartnerApiState,
      dispatch,
      match,
      history,
      locationQuery,
    } = this.props;

    if (getUtilityPartnerApiState.inProgress) {
      return <div className="loading-ellipses" />;
    }

    if (userUtilityActivation && isUtilityActivationActive(userUtilityActivation)) {
      const link = getRenterUtilityLink({
        partnerSlug: userUtilityActivation.partner.slug,
        shortId: userUtilityActivation.shortId,
      });

      return <Redirect to={link} />;
    }

    if (
      lazySignupUtilityActivation &&
      isUtilityActivationActive(lazySignupUtilityActivation) &&
      locationQuery.noRedirect !== 'true'
    ) {
      const link = getRenterUtilityLink({
        partnerSlug: lazySignupUtilityActivation.partner.slug,
        shortId: lazySignupUtilityActivation.shortId,
      });

      return <Redirect to={link} />;
    }

    if (currentUser && shouldRedirectToProfilePage(currentUser)) {
      return <Redirect to="/profile?utilitySetup=true" />;
    }

    if (!utilityPartner) {
      return <div>Utility Partner {match.params.partnerSlug} not found.</div>;
    }

    return (
      <SrUtilitySetupAddressSearchPage
        activationLinkRequest={activationLinkRequest}
        utilityUser={utilityUser}
        utilityPartner={utilityPartner}
        recentlyUnlistedAddresses={recentlyUnlistedAddresses}
        dispatch={dispatch}
        history={history}
      />
    );
  }
}

UtilitySetupAddressSearchPage.displayName = 'UtilitySetupAddressSearchPage';

export default UtilitySetupAddressSearchPage;
